import { useEffect, useState, type ReactElement } from 'react';
import { ArchiveRestore, Search, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { ArchivedCard, ArchivedList, ArchivePage, ArchiveType } from '@molt/shared';
import { Button, EmptyState, Notice, TextField } from '@/components';
import { announce } from '@/lib/announce';
import { boardArchiveQueryOptions } from '@/lib/archive';
import { cardDisplayTitle } from '@/lib/card-title';
import { useDeleteCard, useRestoreCard } from '@/lib/cards';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { boardListsQueryOptions, useDeleteList, useRestoreList } from '@/lib/lists';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';
import { ArchiveDeleteConfirm, type ArchiveDeleteTarget } from './ArchiveDeleteConfirm';
import { ArchiveRestoreDestinationDialog } from './ArchiveRestoreDestinationDialog';

export interface ArchivedItemsPanelProps {
  readonly boardId: string;
  readonly canManageArchive: boolean;
  readonly disabledReason: string | null;
  readonly onPermanentListDelete?: ((listId: string) => void) | undefined;
}

interface LoadedArchivePage {
  readonly cursor: string | undefined;
  readonly page: ArchivePage;
}

/**
 * ⚠ NOT a local `Untitled list` twin of `cardDisplayTitle`. There is no
 * `listDisplayTitle` leaf to extract into: five other files author this same
 * fallback (`HomePage`, `ArchiveDeleteConfirm`, `ListCopyDialog` twice,
 * `BoardFace`) and none of them is this slice's, so a shared leaf built with
 * one caller would claim ownership of nodes it cannot see. Declared under this
 * panel's own namespace and returned as a residual instead —
 * `myCards.untitledList` is a route namespace and is not reachable from here,
 * which is exactly the refusal `lib/card-title.ts` already records.
 *
 * ⚠ `t` is the REQUIRED first parameter here and on `archiveDate`, for the
 * reason `cardDisplayTitle` states: a defaulted translator lets one call site
 * fall through to a default that looks correct.
 */
function archivedListTitle(t: LocaleFormattingTranslate, title: string): string {
  return title.trim() === '' ? t('archivedItems.untitledList') : title;
}

/**
 * ⚠ `toLocaleDateString(undefined, …)` formats in the BROWSER's locale, not
 * the viewer's chosen one — `lib/locale-formatting.ts` is where that is fixed
 * and this slice does not fix it (the board-table slice returned the identical
 * residual for `dueDateLabel`). What routing buys is that the SENTENCE around
 * the date is now one slotted value, so the later fix has exactly one place to
 * land and a translator can move the date inside it.
 */
function archiveDate(t: LocaleFormattingTranslate, value: string | null): string {
  if (value === null) return t('archivedItems.archivedWithList');
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return t('archivedItems.archivedNoDate');
  return t('archivedItems.archivedOn', {
    date: date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  });
}

interface CardArchiveRowProps {
  readonly card: ArchivedCard;
  readonly canManageArchive: boolean;
  readonly disabledReason: string | null;
  readonly onRestore: (card: ArchivedCard) => void;
  readonly onDelete: (target: ArchiveDeleteTarget) => void;
}

function CardArchiveRow({
  card,
  canManageArchive,
  disabledReason,
  onRestore,
  onDelete,
}: CardArchiveRowProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const title = cardDisplayTitle(t, card.title);
  /*
   * ⚠ REUSED, NOT DECLARED. `BoardMenuPanel.tsx:715` already resolves
   * `boardMenu.reason.editorArchive` into the `disabledReason` prop this
   * expression falls back from. Two authors for one node is the
   * `boardTable.viewLabel` defect: served a catalogue, the composed branch
   * speaks the translation and the `??` branch speaks English, with nothing
   * red anywhere.
   */
  const manageDisabledReason = disabledReason ?? t('boardMenu.reason.editorArchive');
  const deleteDisabledReason = !canManageArchive
    ? manageDisabledReason
    : card.archived
      ? null
      : t('archivedItems.reason.archiveFirst');
  const deleteButtonProps =
    deleteDisabledReason === null ? {} : { disabledReason: deleteDisabledReason };

  return (
    <li className="rounded-card border border-border bg-surface-raised p-3">
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <p className="line-clamp-2 break-words text-sm font-medium text-ink">{title}</p>
          <p className="text-xs text-ink-muted">
            {card.list.title} · {archiveDate(t, card.archivedAt)}
          </p>
          {card.list.archived ? (
            <p className="mt-1 text-xs text-ink-muted">
              {t('archivedItems.listArchived')}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={!canManageArchive}
            disabledReason={manageDisabledReason}
            aria-label={t('archivedItems.restoreLabel', { title })}
            iconStart={<ArchiveRestore aria-hidden="true" className="size-3.5" />}
            onClick={() => onRestore(card)}
          >
            {t('archivedItems.sendToBoard')}
          </Button>
          <Button
            size="sm"
            variant="quiet"
            className="text-bad"
            disabled={!canManageArchive || deleteDisabledReason !== null}
            aria-label={t('archivedItems.deleteCardLabel', { title })}
            {...deleteButtonProps}
            iconStart={<Trash2 aria-hidden="true" className="size-3.5" />}
            onClick={() => onDelete({ type: 'card', id: card.id, title })}
          >
            {t('archivedItems.delete')}
          </Button>
        </div>
      </div>
    </li>
  );
}

interface ListArchiveRowProps {
  readonly list: ArchivedList;
  readonly canManageArchive: boolean;
  readonly disabledReason: string | null;
  readonly onRestore: (list: ArchivedList) => void;
  readonly onDelete: (target: ArchiveDeleteTarget) => void;
}

function ListArchiveRow({
  list,
  canManageArchive,
  disabledReason,
  onRestore,
  onDelete,
}: ListArchiveRowProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const title = archivedListTitle(t, list.title);
  const manageDisabledReason = disabledReason ?? t('boardMenu.reason.editorArchive');

  return (
    <li className="rounded-card border border-border bg-surface-raised p-3">
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <p className="line-clamp-2 break-words text-sm font-medium text-ink">{title}</p>
          {/*
            ⚠ REUSED, NOT DECLARED. `boardFace.list.cardCount` is the ONE
            declaration of this sentence — the list-header slice already shares
            it between `ListHeader`'s expanded header and `ListColumn`'s
            collapsed rail, and pins it as the only one. This panel opens OVER
            the board face, so a second declaration would let one list be
            counted two ways in the same frame.

            ⚠ The count is passed TWICE — as the `{count}` slot AND as `t`'s
            third positional argument. `interfaceMessage` takes the arity from
            the third argument alone and never reads it out of `values`, so a
            two-argument call renders `1 cards` on a one-card list, with `count`
            optional and `tsc` green.
          */}
          <p className="text-xs text-ink-muted">
            {archiveDate(t, list.archivedAt)} ·{' '}
            {t('boardFace.list.cardCount', { count: list.cardCount }, list.cardCount)}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          <Button
            size="sm"
            variant="secondary"
            disabled={!canManageArchive}
            disabledReason={manageDisabledReason}
            aria-label={t('archivedItems.restoreLabel', { title })}
            iconStart={<ArchiveRestore aria-hidden="true" className="size-3.5" />}
            onClick={() => onRestore(list)}
          >
            {t('archivedItems.sendToBoard')}
          </Button>
          <Button
            size="sm"
            variant="quiet"
            className="text-bad"
            disabled={!canManageArchive}
            disabledReason={manageDisabledReason}
            aria-label={t('archivedItems.deleteListLabel', { title })}
            iconStart={<Trash2 aria-hidden="true" className="size-3.5" />}
            onClick={() =>
              onDelete({
                type: 'list',
                id: list.id,
                title,
                cardCount: list.cardCount,
              })
            }
          >
            {t('archivedItems.delete')}
          </Button>
        </div>
      </div>
    </li>
  );
}

export function ArchivedItemsPanel({
  boardId,
  canManageArchive,
  disabledReason,
  onPermanentListDelete,
}: ArchivedItemsPanelProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [type, setType] = useState<ArchiveType>('cards');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pages, setPages] = useState<LoadedArchivePage[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<ArchiveDeleteTarget | null>(null);
  const [restoreDestinationCard, setRestoreDestinationCard] =
    useState<ArchivedCard | null>(null);

  const archive = useQuery(
    boardArchiveQueryOptions({ boardId, type, q, cursor, limit: 50, enabled: true }),
  );
  const openLists = useQuery({
    ...boardListsQueryOptions(boardId, 'open'),
    enabled: restoreDestinationCard !== null,
  });
  const restoreCard = useRestoreCard(boardId);
  const deleteCard = useDeleteCard(boardId);
  const restoreList = useRestoreList(boardId);
  const deleteList = useDeleteList(boardId);

  useEffect(() => {
    setCursor(undefined);
    setPages([]);
  }, [type, q]);

  useEffect(() => {
    if (archive.data === undefined) return;
    setPages((previous) => {
      const next = { cursor, page: archive.data };
      if (cursor === undefined) return [next];
      const existingIndex = previous.findIndex((entry) => entry.cursor === cursor);
      if (existingIndex === -1) return [...previous, next];
      return previous.map((entry, index) => (index === existingIndex ? next : entry));
    });
  }, [archive.data, cursor]);

  function removeArchivedCard(cardId: string): void {
    setPages((previous) =>
      previous.map((entry) =>
        entry.page.type === 'cards'
          ? {
              ...entry,
              page: {
                ...entry.page,
                items: entry.page.items.filter((card) => card.id !== cardId),
              },
            }
          : entry,
      ),
    );
  }

  function removeArchivedList(listId: string): void {
    setPages((previous) =>
      previous.map((entry) =>
        entry.page.type === 'lists'
          ? {
              ...entry,
              page: {
                ...entry.page,
                items: entry.page.items.filter((list) => list.id !== listId),
              },
            }
          : entry,
      ),
    );
  }

  function handleRestoreCard(card: ArchivedCard): void {
    if (!canManageArchive) return;
    const title = cardDisplayTitle(t, card.title);
    if (!card.restorableInPlace) {
      setRestoreDestinationCard(card);
      return;
    }
    void restoreCard
      .mutateAsync({ cardId: card.id })
      .then(() => {
        removeArchivedCard(card.id);
        announce(t('archivedItems.announce.cardRestored', { title }));
      })
      .catch(() => undefined);
  }

  function handleRestoreList(list: ArchivedList): void {
    if (!canManageArchive) return;
    void restoreList
      .mutateAsync({ listId: list.id, title: list.title })
      .then(() => {
        removeArchivedList(list.id);
        /*
         * ⚠ THE RAW `list.title`, NOT `archivedListTitle(t, …)`, and that is
         * PRESERVED rather than fixed. The three sibling `announce` sites all
         * pass a resolved title; this one has always announced an untitled
         * list as `List  was sent to the board.` Routing is not the place to
         * change what a live region says — it would move bytes a spec may
         * bind — so the divergence is returned as a residual instead.
         */
        announce(t('archivedItems.announce.listRestored', { title: list.title }));
      })
      .catch(() => undefined);
  }

  function handleConfirmDelete(): void {
    if (deleteTarget === null) return;
    if (deleteTarget.type === 'card') {
      void deleteCard
        .mutateAsync({ cardId: deleteTarget.id, title: deleteTarget.title })
        .then(() => {
          removeArchivedCard(deleteTarget.id);
          announce(
            t('archivedItems.announce.cardDeleted', { title: deleteTarget.title }),
          );
          setDeleteTarget(null);
        })
        .catch(() => undefined);
      return;
    }

    void deleteList
      .mutateAsync({
        listId: deleteTarget.id,
        title: deleteTarget.title,
        cardCount: deleteTarget.cardCount,
      })
      .then(() => {
        removeArchivedList(deleteTarget.id);
        onPermanentListDelete?.(deleteTarget.id);
        announce(t('archivedItems.announce.listDeleted', { title: deleteTarget.title }));
        setDeleteTarget(null);
      })
      .catch(() => undefined);
  }

  const cardRows =
    type === 'cards'
      ? pages.flatMap((entry) => (entry.page.type === 'cards' ? entry.page.items : []))
      : [];
  const listRows =
    type === 'lists'
      ? pages.flatMap((entry) => (entry.page.type === 'lists' ? entry.page.items : []))
      : [];
  const rowCount = type === 'cards' ? cardRows.length : listRows.length;
  const nextCursor = pages.at(-1)?.page.nextCursor ?? null;
  const busyDeleting = deleteCard.isPending || deleteList.isPending;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div
        role="group"
        aria-label={t('archivedItems.typeGroup')}
        className="grid grid-cols-2 gap-1 rounded-card bg-surface-raised p-1"
      >
        <Button
          variant="toggle"
          size="sm"
          pressed={type === 'cards'}
          onClick={() => setType('cards')}
        >
          {t('archivedItems.cards')}
        </Button>
        <Button
          variant="toggle"
          size="sm"
          pressed={type === 'lists'}
          onClick={() => setType('lists')}
        >
          {t('archivedItems.lists')}
        </Button>
      </div>

      <TextField
        label={t('archivedItems.search')}
        value={q}
        onChange={(event) => setQ(event.currentTarget.value)}
        leadingIcon={<Search aria-hidden="true" className="size-4" />}
        clearable
        onClear={() => setQ('')}
      />

      {!canManageArchive && disabledReason !== null ? (
        <Notice tone="locked" body={disabledReason} />
      ) : null}

      {archive.isError ? (
        <EmptyState
          tone="error"
          size="inline"
          headingLevel={3}
          title={t('archivedItems.error.title')}
          body={t('archivedItems.error.body')}
          action={
            <Button size="sm" onClick={() => void archive.refetch()}>
              {t('archivedItems.retry')}
            </Button>
          }
        />
      ) : archive.isLoading ? (
        <p className="text-sm text-ink-muted">{t('archivedItems.loading')}</p>
      ) : rowCount === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          headingLevel={3}
          title={
            q.trim() === ''
              ? t('archivedItems.empty.title')
              : t('archivedItems.noMatch.title')
          }
          body={
            q.trim() === ''
              ? t('archivedItems.empty.body')
              : t('archivedItems.noMatch.body')
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {type === 'cards'
            ? cardRows.map((card) => (
                <CardArchiveRow
                  key={card.id}
                  card={card}
                  canManageArchive={canManageArchive}
                  disabledReason={disabledReason}
                  onRestore={handleRestoreCard}
                  onDelete={setDeleteTarget}
                />
              ))
            : listRows.map((list) => (
                <ListArchiveRow
                  key={list.id}
                  list={list}
                  canManageArchive={canManageArchive}
                  disabledReason={disabledReason}
                  onRestore={handleRestoreList}
                  onDelete={setDeleteTarget}
                />
              ))}
        </ul>
      )}

      {nextCursor !== null ? (
        <Button
          size="sm"
          variant="secondary"
          busy={archive.isFetching && cursor !== undefined}
          busyLabel={t('archivedItems.loadMoreBusy')}
          onClick={() => setCursor(nextCursor)}
        >
          {t('archivedItems.loadMore')}
        </Button>
      ) : null}

      <ArchiveDeleteConfirm
        target={deleteTarget}
        busy={busyDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/*
        ⚠ `?? ''` and not `?? 'Untitled card'`: the original was a NULLISH
        fallback while `cardDisplayTitle`'s is a trim-empty one, and the empty
        string routes through the same predicate to the same word. The dialog
        itself is `ArchiveRestoreDestinationDialog.tsx`, which this slice does
        not own and which authors its own unrouted `Untitled card` — returned.
      */}
      <ArchiveRestoreDestinationDialog
        open={restoreDestinationCard !== null}
        cardTitle={cardDisplayTitle(t, restoreDestinationCard?.title ?? '')}
        lists={openLists.data ?? []}
        busy={restoreCard.isPending}
        onCancel={() => setRestoreDestinationCard(null)}
        onRestore={(listId) => {
          if (restoreDestinationCard === null) return;
          const title = cardDisplayTitle(t, restoreDestinationCard.title);
          void restoreCard
            .mutateAsync({ cardId: restoreDestinationCard.id, listId })
            .then(() => {
              removeArchivedCard(restoreDestinationCard.id);
              announce(t('archivedItems.announce.cardRestored', { title }));
              setRestoreDestinationCard(null);
            })
            .catch(() => undefined);
        }}
      />
    </div>
  );
}
